import { showToast } from '@design-system';
import { ApiFile } from '@hooks';
import {
    EcsModule,
    EventFeedbackToCreate,
    EventFeedbackType,
    GetUsersResponse,
    useCreateEventFeedbackMutation,
    useGetEventFeedbackTypesMutation,
    useGetUsersMutation
} from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventFeedbackPageProps } from './EventFeedbackPage';

type EventDestination = { id: EcsModule; label: string };

export const useEventFeedbackPageVM = ({ eventDestination: event_destination }: EventFeedbackPageProps) => {
    const [eventFeedbackToCreate, setEventFeedbackToCreate] = useState<EventFeedbackToCreate>({
        operator_id: '',
        user_id: '',
        worksite_id: '',
        event_destination: event_destination || '',
        event_type: '',
        comment: '',
        files: []
    });

    const [filteredEventTypes, setFilteredEventTypes] = useState<EventFeedbackType[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<
        (GetUsersResponse['operators'][number] & {
            id: string;
            label: string;
        })[]
    >([]);
    const [files, setFiles] = useState<ApiFile[]>([]);

    const destinations: EventDestination[] = [
        { id: 'rh', label: 'Richesses Humaines' },
        { id: 'sst', label: 'Santé Sécurité au Travail' },
        { id: 'chantier', label: 'Chantier' }
    ];

    const navigate = useNavigate();

    const [getEventFeedbackTypes, { isLoading: isLoadingEventFeedbackTypes, data: eventFeedbackTypes, error: eventFeedbackTypesError }] =
        useGetEventFeedbackTypesMutation();
    const [getOperators, { isLoading: isLoadingOperators, data: operators, error: operatorsError }] = useGetUsersMutation();

    const [
        createEventFeedback,
        { isLoading: isLoadingCreateEventFeedback, error: createEventFeedbackError, isSuccess: isSuccessCreateEventFeedback }
    ] = useCreateEventFeedbackMutation();

    const handleFieldChange = (field: keyof EventFeedbackToCreate, value: string) => {
        if (field === 'operator_id') {
            setWorksitesOptions([]);
            setEventFeedbackToCreate((prev) => ({
                ...prev,
                [field]: value,
                worksite_id: ''
            }));

            const selectedOperator = operators?.operators.find((op) => op.id_operator === value);
            if (selectedOperator) {
                const worksites = selectedOperator.worksites.map((worksite) => ({
                    id: worksite.id,
                    label: worksite.name
                }));
                setWorksitesOptions(worksites);
            }
        } else if (field === 'event_destination') {
            setEventFeedbackToCreate((prev) => ({
                ...prev,
                [field]: value,
                event_type: ''
            }));
        } else {
            setEventFeedbackToCreate((prev) => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleCreateEventFeedback = () => {
        createEventFeedback({
            ...eventFeedbackToCreate,
            event_type: eventFeedbackTypes?.find((eventType) => eventType.id === parseInt(eventFeedbackToCreate.event_type))?.label || '',
            worksite_id: parseInt(eventFeedbackToCreate.worksite_id),
            files: files.map((file) => ({ id: file.id }))
        });
    };

    const isRhDestination = useMemo(() => eventFeedbackToCreate.event_destination === 'rh', [eventFeedbackToCreate]);

    const isFeedBackFilled = useMemo(
        () =>
            (eventFeedbackToCreate.operator_id || isRhDestination) &&
            (eventFeedbackToCreate.worksite_id || isRhDestination) &&
            eventFeedbackToCreate.event_type &&
            eventFeedbackToCreate.event_destination &&
            (eventFeedbackToCreate.comment.length > 0 || files.length > 0),
        [eventFeedbackToCreate, files, isRhDestination]
    );

    useEffect(() => {
        getEventFeedbackTypes();
        getOperators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (eventFeedbackTypes) {
            setFilteredEventTypes(
                eventFeedbackTypes.filter((eventType) => eventType.event_destination === eventFeedbackToCreate.event_destination)
            );
        }
    }, [eventFeedbackTypes, eventFeedbackToCreate.event_destination]);

    useEffect(() => {
        if (operators) {
            setOperatorsOptions(
                operators.operators.map((operator) => ({
                    ...operator,
                    id: operator.id_operator,
                    label: operator.name
                }))
            );
        }
    }, [operators]);

    useEffect(() => {
        if (operators?.operators && eventFeedbackToCreate.operator_id) {
            const selectedOperator = operators.operators.find((op) => op.id_operator === eventFeedbackToCreate.operator_id);

            if (selectedOperator) {
                const worksites = selectedOperator.worksites.map((worksite) => ({
                    id: worksite.id,
                    label: worksite.name
                }));
                setWorksitesOptions(worksites);
            }
        }
    }, [operators, eventFeedbackToCreate.operator_id]);

    useEffect(() => {
        if (isSuccessCreateEventFeedback) {
            showToast({
                message: 'Évènement remonté avec succès',
                type: 'success'
            });

            navigate('/home');
        }
    }, [isSuccessCreateEventFeedback, createEventFeedbackError, isLoadingCreateEventFeedback, navigate]);

    return {
        eventFeedbackToCreate,
        operatorsOptions,
        worksitesOptions,
        handleFieldChange,
        filteredEventTypes,
        isLoadingEventFeedbackTypes,
        eventFeedbackTypesError,
        operatorsError,
        isLoadingOperators,
        destinations,
        files,
        setFiles,
        handleCreateEventFeedback,
        isFeedBackFilled,
        isRhDestination
    };
};
